import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Link, Stack, Typography } from '@mui/material'
import studentApps from '../../data/studentApps'
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCenter,
  DragOverlay,
  defaultDropAnimationSideEffects,
} from '@dnd-kit/core'
import {
  SortableContext,
  horizontalListSortingStrategy,
  arrayMove,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

// ─── Visual App Card (Used by both Sortable Item and Drag Overlay) ───────────
const AppCardView = React.memo(React.forwardRef(({ app, isOverlay, isPlaceholder, wasDragged }, ref) => {
  const cardRef = useRef(null)

  // Only enable 3D tilt on non-touch (desktop) devices to avoid iOS compositing flicker
  const isTouchDevice = typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0)

  const handleMouseEnter = () => {
    if (isOverlay || isPlaceholder || isTouchDevice) return
    const card = cardRef.current
    if (!card) return
    card.style.transition = 'transform 0.25s ease-out'
    card.style.transform = 'scale(1.06)'
  }

  const handleMouseMove = (e) => {
    if (isOverlay || isPlaceholder || isTouchDevice) return
    const card = cardRef.current
    if (!card) return
    const rect = card.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    const centerX = rect.width / 2
    const centerY = rect.height / 2
    const rotateY = ((x - centerX) / centerX) * 12
    const rotateX = ((centerY - y) / centerY) * 12

    card.style.transition = 'transform 0.1s ease-out'
    card.style.transform = `perspective(600px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(1.06)`

    const glare = card.querySelector('.glare-overlay')
    if (glare) {
      const pctX = (x / rect.width) * 100
      const pctY = (y / rect.height) * 100
      glare.style.background = `radial-gradient(circle at ${pctX}% ${pctY}%, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0.12) 40%, transparent 70%)`
      glare.style.opacity = '1'
    }
  }

  const handleMouseLeave = () => {
    if (isTouchDevice) return
    const card = cardRef.current
    if (!card) return
    card.style.transition = 'transform 0.35s ease-out'
    card.style.transform = 'none'
    const glare = card.querySelector('.glare-overlay')
    if (glare) {
      glare.style.opacity = '0'
    }
  }

  const handleLinkClick = (e) => {
    if (isOverlay || isPlaceholder) {
      e.preventDefault()
      return
    }
    if (wasDragged && wasDragged.current) {
      e.preventDefault()
    }
  }

  return (
    <Box ref={ref} sx={{ position: 'relative', WebkitTouchCallout: 'none' }}>
      <Link
        href={app.link}
        target="_blank"
        underline="none"
        color="inherit"
        draggable="false"
        onClick={handleLinkClick}
      >
        <Stack
          spacing={1.2}
          sx={{
            cursor: isOverlay ? 'grabbing' : isPlaceholder ? 'grab' : 'pointer',
            alignItems: 'center',
            width: { xs: 125, sm: 185 },
            flexShrink: 0,
            animation: isOverlay ? 'jiggle 0.3s ease-in-out infinite' : 'none',
            opacity: isPlaceholder ? 0.3 : 1, // Dim the placeholder in the list
          }}
        >
          <Box
            ref={cardRef}
            onMouseEnter={handleMouseEnter}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            sx={{
              position: 'relative',
              width: { xs: 105, sm: 170 },
              height: { xs: 105, sm: 170 },
              borderRadius: '22%',
              overflow: 'hidden',
              transition: 'transform 0.15s ease-out',
              boxShadow: isOverlay ? '0 24px 48px rgba(0,0,0,0.3)' : '0 4px 16px rgba(0,0,0,0.12)',
              transform: isOverlay ? 'scale(1.05)' : 'none',
            }}
          >
            {app.icon ? (
              <Box component="img" src={app.icon} alt={app.name} loading="lazy" draggable="false" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            ) : (
              <Box sx={{ width: '100%', height: '100%', backgroundColor: '#D9D9D9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography sx={{ color: '#999', fontSize: 14 }}>{app.name[0]}</Typography>
              </Box>
            )}
            <Box className="glare-overlay" sx={{ position: 'absolute', inset: 0, borderRadius: 'inherit', pointerEvents: 'none', opacity: 0, transition: 'opacity 0.25s ease-out', mixBlendMode: 'overlay' }} />
          </Box>

          <Box sx={{ textAlign: 'center', height: { xs: 80, sm: 96 }, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
            <Typography sx={{ fontWeight: 600, fontSize: { xs: 13, md: 16 }, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
              {app.name}
            </Typography>
            <Typography sx={{ fontWeight: 300, fontSize: { xs: 10.5, md: 12 }, lineHeight: 1.3, mt: 0.3, color: '#777', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical' }}>
              {app.team}
            </Typography>
          </Box>
        </Stack>
      </Link>
    </Box>
  )
}))

// ─── Sortable Wrapper ───────────────────────────────────────────────────────
const SortableAppCard = React.memo(({ id, app }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  const wasDragged = useRef(false)

  // Track when a drag actually happened
  useEffect(() => {
    if (isDragging) {
      wasDragged.current = true
    }
  }, [isDragging])

  // Reset the flag after a delay so the click event (which fires after drag end) is blocked
  const prevIsDragging = useRef(false)
  useEffect(() => {
    if (prevIsDragging.current && !isDragging) {
      // isDragging just turned false → drag ended, reset after click would have fired
      const timer = setTimeout(() => {
        wasDragged.current = false
      }, 300)
      return () => clearTimeout(timer)
    }
    prevIsDragging.current = isDragging
  }, [isDragging])

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    zIndex: isDragging ? 2 : 1,
    touchAction: 'pan-x pan-y',
  }

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <AppCardView app={app} isPlaceholder={isDragging} wasDragged={wasDragged} />
    </div>
  )
})

// ─── Main section ───────────────────────────────────────────────────────────
const StudentApps = () => {
  const scrollRef = useRef(null)
  const isHoveredRef = useRef(false)
  const isUserInteractingRef = useRef(false)
  const isDraggingRef = useRef(false)
  const timerRef = useRef(null)

  const [activeId, setActiveId] = useState(null)

  const [appsWithIcons, setAppsWithIcons] = useState(() => {
    let validApps = studentApps.filter((app) => app.icon !== null)
    for (let i = validApps.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [validApps[i], validApps[j]] = [validApps[j], validApps[i]];
    }
    validApps.sort((a, b) => {
      const getTeamSize = (teamStr) => {
        const commas = (teamStr.match(/,/g) || []).length
        const ands = (teamStr.match(/ e /g) || []).length
        return commas + ands + 1
      }
      const aIsLarge = getTeamSize(a.team) > 2 ? 1 : 0
      const bIsLarge = getTeamSize(b.team) > 2 ? 1 : 0
      return bIsLarge - aIsLarge
    })
    return validApps
  })

  const duplicatedAppsWithId = useMemo(() => {
    const first = appsWithIcons.map((app) => ({ ...app, uniqueId: `${app.name}-0` }))
    const second = appsWithIcons.map((app) => ({ ...app, uniqueId: `${app.name}-1` }))
    const third = appsWithIcons.map((app) => ({ ...app, uniqueId: `${app.name}-2` }))
    return [...first, ...second, ...third]
  }, [appsWithIcons])

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 400,
        tolerance: 5,
      },
    })
  )

  const handleDragStart = (event) => {
    isDraggingRef.current = true
    setActiveId(event.active.id)
  }

  const handleDragEnd = (event) => {
    const { active, over } = event
    if (active && over && active.id !== over.id) {
      const oldIndex = duplicatedAppsWithId.findIndex((a) => a.uniqueId === active.id) % appsWithIcons.length
      const newIndex = duplicatedAppsWithId.findIndex((a) => a.uniqueId === over.id) % appsWithIcons.length
      setAppsWithIcons((items) => arrayMove(items, oldIndex, newIndex))
    }
    setActiveId(null)
    // Wait for drop animation to settle before resuming scroll
    setTimeout(() => {
      isDraggingRef.current = false
    }, 400)
  }

  const handleDragCancel = () => {
    setActiveId(null)
    setTimeout(() => {
      isDraggingRef.current = false
    }, 400)
  }

  const activeApp = useMemo(() => {
    if (!activeId) return null
    return duplicatedAppsWithId.find((app) => app.uniqueId === activeId)
  }, [activeId, duplicatedAppsWithId])

  const dropAnimation = {
    duration: 350,
    easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
    sideEffects: defaultDropAnimationSideEffects({
      styles: {
        active: {
          opacity: '0.4',
        },
      },
    }),
  }

  useEffect(() => {
    let animationFrameId
    const isMobile = window.innerWidth < 600
    const baseSpeed = isMobile ? 0.5 : 0.8
    let currentSpeed = baseSpeed
    const maxSpeed = baseSpeed
    const acceleration = 0.02

    // Safari truncates sub-pixel scrollLeft (el.scrollLeft += 0.25 stays 0).
    // We accumulate fractional pixels and only assign when we have >= 1px.
    let exactScrollLeft = null
    let fractionalAccumulator = 0

    const step = () => {
      const el = scrollRef.current
      if (el) {
        const isPaused = isHoveredRef.current || isUserInteractingRef.current || isDraggingRef.current
        const targetSpeed = isPaused ? 0 : maxSpeed
        if (currentSpeed < targetSpeed) {
          currentSpeed = Math.min(currentSpeed + acceleration, targetSpeed)
        } else if (currentSpeed > targetSpeed) {
          currentSpeed = Math.max(currentSpeed - acceleration, targetSpeed)
        }

        if (exactScrollLeft === null) {
          exactScrollLeft = el.scrollLeft
        }

        // If the user manually scrolled (swipe/wheel), sync our tracker.
        if (Math.abs(exactScrollLeft - el.scrollLeft) > 2) {
          exactScrollLeft = el.scrollLeft
          fractionalAccumulator = 0
        }

        // Initial centering: start in the middle third
        if (exactScrollLeft === 0 && currentSpeed > 0) {
          exactScrollLeft = Math.round(el.scrollWidth / 3)
          el.scrollLeft = exactScrollLeft
        }

        if (currentSpeed > 0 || isUserInteractingRef.current) {
          if (!isUserInteractingRef.current) {
            // Accumulate fractional pixels, only assign whole-pixel increments
            // This prevents Safari from rounding sub-pixel values and causing flicker
            fractionalAccumulator += currentSpeed
            const wholePx = Math.floor(fractionalAccumulator)
            if (wholePx >= 1) {
              fractionalAccumulator -= wholePx
              exactScrollLeft += wholePx
              el.scrollLeft = exactScrollLeft
            }
          }

          const singleWidth = el.scrollWidth / 3
          if (singleWidth > 0) {
            // Infinite loop: wrap scroll position between 1/3 and 2/3
            if (exactScrollLeft >= singleWidth * 2) {
              exactScrollLeft -= Math.round(singleWidth)
              el.scrollLeft = exactScrollLeft
            } else if (exactScrollLeft <= 0) {
              exactScrollLeft += Math.round(singleWidth)
              el.scrollLeft = exactScrollLeft
            }
          }
        }
      }
      animationFrameId = requestAnimationFrame(step)
    }
    animationFrameId = requestAnimationFrame(step)
    return () => cancelAnimationFrame(animationFrameId)
  }, [])

  const triggerUserInteraction = () => {
    isUserInteractingRef.current = true
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => { isUserInteractingRef.current = false }, 3000)
  }

  return (
    <Box sx={{ backgroundColor: '#FFFFFF', display: 'flex', flexDirection: 'column', alignItems: 'center', pt: { md: 10, xs: 6 }, pb: { md: 10, xs: 6 }, px: 0, width: '100%', overflow: 'hidden' }}>
      <Box sx={{ width: '100%', mb: 5, px: { xs: 3, md: '12.5vw' }, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="sectionTitle" component="div" sx={{ textAlign: 'center', mb: 1 }}>Apps desenvolvidos pelos nossos estudantes!</Typography>
        <Typography variant="sectionSubtitle" component="div" sx={{ textAlign: 'center', px: 0 }}>Conheça alguns dos apps dessa turma que já estão disponíveis para download.</Typography>
      </Box>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext items={duplicatedAppsWithId.map(a => a.uniqueId)} strategy={horizontalListSortingStrategy}>
          <Box
            ref={scrollRef}
            onPointerEnter={(e) => { if (e.pointerType === 'mouse') isHoveredRef.current = true }}
            onPointerLeave={(e) => { if (e.pointerType === 'mouse') isHoveredRef.current = false }}
            onMouseDown={triggerUserInteraction}
            onWheel={triggerUserInteraction}
            onTouchStart={triggerUserInteraction}
            onTouchMove={triggerUserInteraction}
            sx={{
              display: 'flex',
              gap: { xs: 2, sm: 3, md: 5 },
              width: '100%',
              overflowX: 'auto',
              py: 2,
              px: { xs: 2, md: 4 },
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
              touchAction: 'pan-x pan-y',
              cursor: 'grab',
              '&:active': { cursor: 'grabbing' },
              overscrollBehaviorX: 'none',
              userSelect: 'none',
              WebkitUserSelect: 'none',
            }}
          >
            {duplicatedAppsWithId.map((app) => (
              <SortableAppCard key={app.uniqueId} id={app.uniqueId} app={app} />
            ))}
          </Box>
        </SortableContext>
        <DragOverlay dropAnimation={dropAnimation}>
          {activeApp ? <AppCardView app={activeApp} isOverlay={true} /> : null}
        </DragOverlay>
      </DndContext>
    </Box>
  )
}

export default StudentApps
